import isEqual from 'lodash/isEqual';
import { useDispatch, useSelector } from 'react-redux';
import { selectDoesUserHaveRbacPermission } from 'core/userContext/selectors';
import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import {
  updateNodeData,
  updateWorkflow,
  getIsJourneyNewUserEntrySourceEnabled,
} from 'modules/workflows/ducks';
import { useDisplayCloneErrors } from 'modules/workflows/hooks';
import { SearchCombo } from 'modules/workflows/types';
import { Subtitle2 } from 'ui/theme';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { SchedulerFormProvider } from './ScheduleEntrySource/Scheduler/ScheduleFormProvider';
import { START_NODE_EDITOR_TEST_ID } from './StartNodeEditor.constants';
import { StartNodeContextProvider, useStartNodeContext } from './StartNodeEditor.context';
import {
  SearchQuery,
  useClonedQueryErrors,
  useGetEntrySourceMetadata,
  useGetSchedulerFormData,
} from './StartNodeEditor.hooks';
import { EntrySource, SourceSetup } from './StartNodeEditor.styled';
import { StartNodeEditorProps } from './StartNodeEditor.types';
import {
  getEntrySourceComponent,
  getStartNodeEditorNodeDataToBeCompared,
  parseNodeDataForSaving,
  parseWorkflowForSaving,
} from './StartNodeEditor.utils';

import { EntranceLimitForm } from './EntranceLimitForm';
import { EntryRules } from './EntryRules';
import { SourceSelect } from './SourceSelect';

/**
 * See the Slab documentation for more information on Start Tile components:
 * https://iterable.slab.com/posts/start-tile-editor-components-wlds1ru4
 */
const StartNodeEditor = ({
  nodeId = '',
  clonedFromNodeData,
  nodeData: initialNodeData,
  workflowData: initialWorkflowData,
  ...rest
}: StartNodeEditorProps) => {
  const dispatch = useDispatch();
  const canDraftMessaging = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.DraftMessaging,
  );
  const isJourneyNewUserEntrySourceEnabled = useSelector(getIsJourneyNewUserEntrySourceEnabled);

  const { nodeData, setNodeData, workflowData } = useStartNodeContext();
  const { searchCombo, nodeClass } = nodeData;

  const EntrySourceComponent = getEntrySourceComponent(nodeClass);

  /* Update Node Editor ----------------------------------- */
  const getFormattedScheduleData = useGetSchedulerFormData();

  const handleUpdate = () => {
    const updatedNodeData = parseNodeDataForSaving(
      nodeData,
      getFormattedScheduleData(),
      isJourneyNewUserEntrySourceEnabled,
    );
    dispatch(updateWorkflow(parseWorkflowForSaving(workflowData, { nodeData })));
    dispatch(updateNodeData(updatedNodeData, nodeId));
  };

  /* Unsaved Changes Modal ----------------------------------- */
  const getHasUnsavedChanges = () => {
    const cleanedInitialNodeData = getStartNodeEditorNodeDataToBeCompared({
      ...initialNodeData,
      ...initialWorkflowData,
    });
    const updatedNodeData = getStartNodeEditorNodeDataToBeCompared({
      ...nodeData,
      ...getFormattedScheduleData(),
      ...workflowData,
    });

    return !isEqual(cleanedInitialNodeData, updatedNodeData);
  };

  /* Cloning ----------------------------------- */
  const { resourceFieldName = '', dataType = '', hasFilter } = useGetEntrySourceMetadata();

  // First, set search combo with cloned filters
  const searchQueriesErrors = useClonedQueryErrors({
    clonedFromSearchCombo: clonedFromNodeData?.searchCombo,
    initialSearchCombo: initialNodeData?.searchCombo,
    searchQueries: searchCombo?.searchQueries as SearchQuery[],
    updateSearchCombo: (newSearchCombo: SearchCombo) =>
      setNodeData({
        searchCombo: newSearchCombo,
      }),
    dataType,
  });

  // Generate any cloned error messaging
  const { isCloneWarningBannerShown, cloneErrors, cloneDisplayValues } = useDisplayCloneErrors({
    clonedFromNodeData,
    fieldsToValidate: {
      [resourceFieldName]: {
        isValid: nodeData[resourceFieldName],
        displayValue: clonedFromNodeData?.resource?.name,
      },
      searchCombo: {
        isValid: Object.keys(searchQueriesErrors).length === 0,
      },
    },
    nodeClass,
  });

  return (
    <NodeEditor
      {...rest}
      dataTest={START_NODE_EDITOR_TEST_ID}
      onUpdate={handleUpdate}
      getHasUnsavedChanges={getHasUnsavedChanges}
      isCloneWarningBannerShown={isCloneWarningBannerShown}
    >
      <Subtitle2>Entry source</Subtitle2>
      <EntrySource>
        <SourceSelect />
        <SourceSetup>
          <EntrySourceComponent
            hasCloningError={cloneErrors[resourceFieldName]}
            displayValue={cloneDisplayValues[resourceFieldName]}
          >
            {hasFilter && <EntryRules queriesErrors={searchQueriesErrors} />}
          </EntrySourceComponent>
        </SourceSetup>
      </EntrySource>
      {canDraftMessaging && <EntranceLimitForm />}
    </NodeEditor>
  );
};

export default (props: StartNodeEditorProps) => (
  <StartNodeContextProvider {...props}>
    <SchedulerFormProvider>
      <StartNodeEditor {...props} />
    </SchedulerFormProvider>
  </StartNodeContextProvider>
);
